import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Image, X, Loader2, Shield, Film, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { FILM_GENRES, FILM_ROLES, SuggestAIButton, SuggestedTags } from './studioShared';

// Stable per-row id so React keys survive credit add/remove.
export let creditSeq = 0;

export const newCredit = (role) => ({ uid: `credit-${++creditSeq}`, name: '', role });

// Self-contained human FILM upload form. Separate from the music/TUS path so it
// never disturbs the critical music-video upload flow. Posts multipart to
// /api/films/upload and includes the "Suggest with AI" control.
export const FilmUploadForm = ({ axiosInstance, onUploaded }) => {
  const [title, setTitle] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [genre, setGenre] = useState('');
  const [customGenre, setCustomGenre] = useState('');
  const [credits, setCredits] = useState(() => [newCredit('Director')]);
  const [tools, setTools] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [certified, setCertified] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const videoRef = useRef(null);
  const thumbRef = useRef(null);

  const updateCredit = (idx, field, val) => {
    setCredits((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: val } : c)));
  };
  const addCredit = () => setCredits((prev) => [...prev, newCredit('Cast')]);
  const removeCredit = (idx) => setCredits((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const FILM_MAX_BYTES = 500 * 1024 * 1024;
  const onFilmVideoSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setVideoFile(null); return; }
    if (f.size > FILM_MAX_BYTES) {
      toast.error('This film is over 500 MB. Please upload a file under 500 MB.');
      e.target.value = '';
      setVideoFile(null);
      return;
    }
    setVideoFile(f);
  };

  const onThumbSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Thumbnail must be 8 MB or smaller.'); return; }
    setThumbFile(f);
    setThumbPreview(URL.createObjectURL(f));
  };

  const applyAi = (data) => {
    if (data.title) setTitle(data.title);
    if (data.description) setSynopsis((prev) => prev || data.description);
    setSuggestedTags([...new Set(data.tags || [])]);
  };

  const submit = async () => {
    if (!videoFile) { toast.error('Select a film file (MP4 or MOV).'); return; }
    if (videoFile.size > FILM_MAX_BYTES) { toast.error('This film is over 500 MB. Please upload a file under 500 MB.'); return; }
    if (!thumbFile) { toast.error('A poster/thumbnail image is required for films.'); return; }
    if (!title.trim()) { toast.error('Give your film a title.'); return; }
    if (!synopsis.trim()) { toast.error('Add a synopsis.'); return; }
    if (!genre) { toast.error('Pick a film genre.'); return; }
    const finalGenre = genre === 'Other' ? customGenre.trim() : genre;
    if (genre === 'Other' && finalGenre.length < 2) { toast.error('Type your genre (2–30 characters).'); return; }
    const cleanCredits = credits.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), role: c.role }));
    if (cleanCredits.length === 0) { toast.error('Add at least one credit (name + role).'); return; }
    if (!certified) { toast.error('Please certify the content guidelines.'); return; }

    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('synopsis', synopsis.trim());
    fd.append('genre', finalGenre);
    fd.append('credits', JSON.stringify(cleanCredits));
    fd.append('tools_used', tools.trim());
    fd.append('video', videoFile);
    fd.append('thumbnail', thumbFile);

    setUploading(true);
    try {
      await axiosInstance.post('/films/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Film uploaded! It is being scanned by AI moderation and will publish once approved.');
      onUploaded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Film upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="film-upload-form">
      <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-orange-500/10 border border-orange-500/30" data-testid="film-moderation-banner">
        <Shield className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-orange-200 font-medium">AI Content Moderation Active</p>
          <p className="text-xs text-orange-300/70 mt-0.5">All films are automatically scanned for prohibited content (violence, nudity, hate speech, and other violations) before they publish. Runtime is detected automatically from your file.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Film file */}
        <div>
          <Label className="text-zinc-300 mb-2 block text-sm">Film File * <span className="text-zinc-500 font-normal">(MP4 or MOV)</span></Label>
          {videoFile ? (
            <div className="aspect-video bg-zinc-900 rounded-lg flex flex-col items-center justify-center border border-zinc-800">
              <Film className="w-8 h-8 text-orange-400 mb-2" />
              <p className="text-xs text-zinc-300 px-4 text-center truncate max-w-full">{videoFile.name}</p>
              <button onClick={() => setVideoFile(null)} className="mt-2 text-xs text-zinc-500 hover:text-white">Remove</button>
            </div>
          ) : (
            <div onClick={() => videoRef.current?.click()} className="aspect-video bg-zinc-900 border-2 border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors">
              <Film className="w-10 h-10 text-zinc-600 mb-3" />
              <p className="text-zinc-300 text-sm font-medium">Select Film File</p>
              <p className="text-zinc-500 text-[11px] mt-1">Max 500 MB · up to 2 hours</p>
            </div>
          )}
          <p className="text-[11px] text-amber-400/90 mt-1.5" data-testid="film-size-warning">⚠ Film must be under 500 MB (MP4 or MOV).</p>
          <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,.mp4,.mov" onChange={onFilmVideoSelect} className="hidden" data-testid="film-video-input" />
        </div>
        {/* Poster */}
        <div>
          <Label className="text-zinc-300 mb-2 block text-sm">Poster / Thumbnail *</Label>
          {thumbPreview ? (
            <div className="relative aspect-video bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800">
              <img src={thumbPreview} alt="poster" className="w-full h-full object-contain" />
              <button onClick={() => { setThumbFile(null); setThumbPreview(null); }} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full hover:bg-black/80"><X className="w-3.5 h-3.5 text-white" /></button>
            </div>
          ) : (
            <div onClick={() => thumbRef.current?.click()} className="aspect-video bg-zinc-900 border-2 border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors">
              <Image className="w-10 h-10 text-zinc-600 mb-2" />
              <p className="text-zinc-400 text-xs">Upload a poster image</p>
            </div>
          )}
          <input ref={thumbRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onThumbSelect} className="hidden" data-testid="film-thumb-input" />
        </div>
      </div>

      {/* Title + AI */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300 text-sm">Title *</Label>
          <SuggestAIButton axiosInstance={axiosInstance} contentType="film"
            getContext={() => [title, synopsis, genre === 'Other' ? customGenre : genre, tools].filter(Boolean).join('. ')}
            onResult={applyAi} disabled={uploading} />
        </div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Film title"
          className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm focus:border-white" data-testid="film-title-input" />
      </div>

      {/* Synopsis */}
      <div className="space-y-1.5">
        <Label className="text-zinc-300 text-sm">Synopsis *</Label>
        <textarea value={synopsis} onChange={(e) => setSynopsis(e.target.value)} placeholder="What is your film about?" rows={3}
          className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 py-2 text-sm focus:border-white outline-none resize-none" data-testid="film-synopsis-input" />
        <SuggestedTags tags={suggestedTags} onPick={(t) => setSynopsis((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}#${t}`)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-sm">Genre *</Label>
          <Select value={genre} onValueChange={setGenre}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm" data-testid="film-genre-select"><SelectValue placeholder="Select genre" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              {FILM_GENRES.map((g) => (<SelectItem key={g} value={g} className="text-white hover:bg-zinc-800 text-sm">{g}</SelectItem>))}
              <SelectItem value="Other" className="text-white hover:bg-zinc-800 text-sm" data-testid="film-genre-other-option">Other</SelectItem>
            </SelectContent>
          </Select>
          {genre === 'Other' && (
            <Input value={customGenre} onChange={(e) => setCustomGenre(e.target.value.slice(0, 30))} maxLength={30}
              placeholder="Type your genre (e.g. Horror)" autoFocus
              className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm focus:border-white" data-testid="film-custom-genre-input" />
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-zinc-300 text-sm">AI Tools Used</Label>
          <Input value={tools} onChange={(e) => setTools(e.target.value)} placeholder="Runway, Midjourney, ElevenLabs..."
            className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm focus:border-white" data-testid="film-tools-input" />
        </div>
      </div>

      {/* Credits */}
      <div className="space-y-2">
        <Label className="text-zinc-300 text-sm">Credits * <span className="text-zinc-500 font-normal">(at least one)</span></Label>
        {credits.map((c, i) => (
          <div key={c.uid} className="flex gap-2 items-center" data-testid={`film-credit-row-${i}`}>
            <Input value={c.name} onChange={(e) => updateCredit(i, 'name', e.target.value)} placeholder="Name"
              className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm flex-1" data-testid={`film-credit-name-${i}`} />
            <Select value={c.role} onValueChange={(v) => updateCredit(i, 'role', v)}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm w-36" data-testid={`film-credit-role-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-56">
                {FILM_ROLES.map((r) => (<SelectItem key={r} value={r} className="text-white hover:bg-zinc-800 text-sm">{r}</SelectItem>))}
              </SelectContent>
            </Select>
            <button type="button" onClick={() => removeCredit(i)} className="p-2 text-zinc-500 hover:text-rose-400" data-testid={`film-credit-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" onClick={addCredit} className="text-xs flex items-center gap-1 text-orange-400 hover:text-orange-300" data-testid="film-add-credit"><Plus className="w-3.5 h-3.5" /> Add credit</button>
      </div>

      <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800 cursor-pointer" onClick={() => setCertified(!certified)} data-testid="film-content-certification">
        <div className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all ${certified ? 'bg-orange-500 border-orange-500' : 'border-zinc-600'}`}>
          {certified && (<svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>)}
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">I own all rights to this film and certify it contains no prohibited content (nudity, active violence, hate speech, illegal activity, or impersonation).</p>
      </div>

      <Button onClick={submit} disabled={uploading}
        className={`w-full font-semibold rounded-full text-sm ${!uploading ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'}`} data-testid="film-upload-submit-btn">
        {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading film…</>) : (<><Upload className="w-4 h-4 mr-2" />Upload Film</>)}
      </Button>
    </div>
  );
};
